import { PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { combineLatest, of, race, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  timeout
} from 'rxjs/operators';

import { APP_ROUTES } from '@cpd-app/core-module/constants/routes';
import { MyFrontDoorGQL } from '@cpd-app/generated';
import { AuthFacade } from '@cpd-app/state/auth';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { UserProfileFacade } from '@cpd-app/state/user-profile';

import { fromServerAnswers } from './containers/home-page-container/front-door-visibility';
import { doorEntry } from './front-door-entry';

/** Same key the home page keeps a visitor's answer under. */
const FRONT_DOOR_KEY = 'cpdFrontDoor';
const SETTLE_MS = 4000;
const LOOKUP_MS = 3000;
const PROFILE_MS = 4000;

const answeredOnThisDevice = (): boolean => {
  try {
    return !!JSON.parse(localStorage.getItem(FRONT_DOOR_KEY) ?? 'null')?.intent;
  } catch {
    return false;
  }
};

/**
 * FD-2. A first-time visitor who arrives by a direct link meets the front door
 * before the page, and is sent on to that page afterwards (`?next=`).
 *
 * It never holds a route for long: the session and the saved-answer lookup
 * each have a timeout, and anything it cannot decide lets the visitor through.
 * Only on the routes listed in app-routing: shared reports, public pages,
 * sign-in, magic and return-session links and error pages are never guarded.
 */
export const frontDoorGuard: CanActivateFn = (_route, state) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID)) || answeredOnThisDevice()) {
    return true;
  }

  const router = inject(Router);
  const auth = inject(AuthFacade);
  const cognito = inject(CognitoAuthFacade);
  const profile = inject(UserProfileFacade);
  const myFrontDoor = inject(MyFrontDoorGQL);

  const settled$ = race(
    auth.isSessionLoaded$.pipe(filter(Boolean), take(1)),
    timer(SETTLE_MS)
  ).pipe(take(1));

  return settled$.pipe(
    switchMap(() =>
      combineLatest([
        cognito.isAuthenticated$.pipe(take(1)),
        // Asks for the profile if nothing has yet, and waits for it. Pages
        // that do not load it themselves (about, transition, pathway) would
        // otherwise hand the guard an empty profile and let everyone through.
        profile.userProfileLoadIfEmpty$.pipe(
          filter(Boolean),
          take(1),
          timeout({ first: PROFILE_MS }),
          catchError(() => of(null))
        )
      ])
    ),
    switchMap(([signedIn, userProfile]) => {
      const base = {
        signedIn: !!signedIn,
        profileLoaded: !!userProfile,
        skillsSurveyDone: !!userProfile?.isSkillsSurveyComplete,
        answered: false
      };
      if (!doorEntry(state.url, base).redirect) {
        return of(true);
      }
      return myFrontDoor.fetch().pipe(
        take(1),
        timeout({ first: LOOKUP_MS }),
        map((r) => !!fromServerAnswers(r.data?.myFrontDoor)),
        catchError(() => of(false)),
        map((answered) => {
          const entry = doorEntry(state.url, { ...base, answered });
          return entry.redirect
            ? router.createUrlTree([APP_ROUTES.CareerPlanning.BuildTarget], {
                queryParams: { next: entry.next }
              })
            : true;
        })
      );
    }),
    catchError(() => of(true))
  );
};
